import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight, BookOpen } from "lucide-react";
import { fetchArticles } from "@/lib/api";

/**
 * Featured guides for a tool — renders rich article cards (image + excerpt
 * + reading time) for each slug in `tool.relatedArticles`. Replaces the
 * link-list sidebar pattern with a more prominent below-the-tool surface
 * so users can deepen context after running a calculator.
 *
 * Pure DB read via `/api/learn/articles` (already cached client-side).
 */
export default function ToolFeaturedArticles({ tool }) {
    const slugs = tool?.relatedArticles || [];
    const [articles, setArticles] = useState(null);

    useEffect(() => {
        if (!slugs.length) {
            setArticles([]);
            return;
        }
        fetchArticles()
            .then((all) => {
                const bySlug = new Map(all.map((a) => [a.slug, a]));
                const matched = slugs.map((s) => {
                    // Exact match first.
                    if (bySlug.has(s)) return bySlug.get(s);
                    // Fallback: prefix match against canonical long-slug
                    // (tool-config uses short stubs like 'home-inspection-
                    // repair-costs'; the live article slug includes a
                    // topical suffix). This makes the wiring resilient.
                    return all.find((a) => a.slug && a.slug.startsWith(s));
                }).filter(Boolean);
                // Deduplicate (a tool may have two stubs that resolve to one)
                const seen = new Set();
                const deduped = matched.filter((a) => {
                    if (seen.has(a.slug)) return false;
                    seen.add(a.slug);
                    return true;
                });
                setArticles(deduped);
            })
            .catch(() => setArticles([]));
    }, [tool?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!slugs.length) return null;
    if (articles === null) return null;
    if (!articles.length) return null;

    return (
        <section
            className="bl-section py-12 sm:py-16 bg-bl-surface border-t border-bl"
            data-testid="tool-featured-articles"
        >
            <div className="bl-container max-w-5xl">
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <div>
                        <div className="bl-eyebrow inline-flex items-center gap-2">
                            <BookOpen className="h-3.5 w-3.5" />
                            Related guides
                        </div>
                        <h2 className="mt-2 font-display font-semibold text-2xl sm:text-3xl text-bl">
                            Go deeper on the topic.
                        </h2>
                    </div>
                    <Link
                        to="/learn"
                        className="text-sm text-bl-primary hover:underline underline-offset-4 inline-flex items-center gap-1"
                        data-testid="tool-featured-browse-all"
                    >
                        Browse all guides
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                </div>

                <div className="mt-6 grid md:grid-cols-3 gap-5">
                    {articles.slice(0, 3).map((a, i) => (
                        <Link
                            key={a.slug}
                            to={`/learn/${a.slug}`}
                            className="bl-card bl-card-hover block overflow-hidden"
                            data-testid={`tool-featured-article-${a.slug}`}
                            data-reveal="rise-sm"
                            data-reveal-delay={Math.min(5, i + 1)}
                        >
                            {a.hero_image?.url && (
                                <div className="aspect-[16/9] bg-bl-cream overflow-hidden">
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${a.hero_image.url}?w=600`}
                                        alt={a.featured_image_alt || a.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            <div className="p-6">
                                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                                    {a.category}
                                </div>
                                <h3 className="mt-2 font-display font-semibold text-lg text-bl leading-snug">
                                    {a.title}
                                </h3>
                                {a.excerpt && (
                                    <p className="mt-2 text-sm text-bl-muted leading-relaxed line-clamp-3">
                                        {a.excerpt}
                                    </p>
                                )}
                                <div className="mt-4 font-mono-bl text-[10px] text-bl-muted flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> {a.reading_time} min
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
